// Razorpay Checkout JS loader.
//
// THE ONE ALLOWED EXTERNAL SCRIPT — this app otherwise never injects
// third-party <script> tags (see the no-external-scripts posture in
// CLAUDE.md / architecture docs: every asset is self-hosted or bundled).
// Razorpay Checkout is exempt because it is PCI-DSS SAQ-A scoped: card/UPI
// details are entered inside an iframe served from checkout.razorpay.com,
// never touch this origin's DOM or JS context, and Razorpay (not us) carries
// the PCI compliance burden. Loading our own card form instead of their
// script would pull card data into this origin and blow up our PCI scope.
// Do not swap this for a bundled/self-hosted copy — Razorpay requires the
// live script from their origin so it can ship security patches without a
// redeploy on our side.

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (...args: unknown[]) => void) => void;
    };
  }
}

let loadPromise: Promise<void> | null = null;

/**
 * Idempotently injects the Razorpay Checkout script tag. Resolves immediately
 * if `window.Razorpay` is already present (e.g. a prior call already loaded
 * it, or a second UpgradeModal mount). Rejects if the script fails to load
 * (offline, ad-blocker, Razorpay outage) so callers can show an honest error
 * instead of hanging forever.
 */
export function loadRazorpayCheckout(): Promise<void> {
  if (typeof window !== 'undefined' && window.Razorpay) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_SCRIPT_SRC}"]`,
    );
    if (existing) {
      // Another mount already injected the tag; wait for it rather than
      // adding a duplicate.
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Razorpay Checkout script')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null; // allow a retry on next call
      reject(new Error('Failed to load Razorpay Checkout script'));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}
